"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import ShiftCreateModal, { ShiftFormValues } from "../components/shift-create-modal";
import ShiftStatusConfirmModal from "../components/shift-status-confirm-modal";
import ShiftTable, { ShiftRow } from "../components/shift-table";
import ShiftService, { CreateShiftPayload, SHIFT_REQUIRES_CONFIRM_STATUS, ShiftStatusCode } from "../services/shift-service";

const ShiftIndexPage: React.FC = () => {
  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState<ShiftRow | null>(null);
  // `assignmentCount` có giá trị khi BE báo ca còn lượt gán trong tương lai → modal chuyển sang bước xác nhận lần 2.
  const [pendingStatus, setPendingStatus] = useState<{ row: ShiftRow; target: "activate" | "deactivate"; assignmentCount?: number } | null>(null);

  // Services
  const { fetch: listShifts, loading: loadingList } = ShiftService.list();
  const { fetch: createShift, loading: creating } = ShiftService.create();
  const { fetch: updateShift, loading: updating } = ShiftService.update();
  const { fetch: changeStatusShift, loading: changingStatus } = ShiftService.changeStatus();

  const listRef = useRef(listShifts);
  useEffect(() => {
    listRef.current = listShifts;
  }, [listShifts]);

  const load = useCallback(async () => {
    const res = await listRef.current({ status: -1 });
    console.log(res);
    if (res && isSuccess(res)) {
      const list = res.data?.list ?? [];
      setRows(
        list.map((s: any) => ({
          id: s.id,
          name: s.name,
          code: s.code,
          start_time: s.start_time,
          end_time: s.end_time,
          description: s.description,
          created_at: s.created_at,
          statusCode: Number(s.status) as ShiftStatusCode,
        }))
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activeRows = useMemo(() => rows.filter((r) => r.statusCode === ShiftStatusCode.ACTIVE), [rows]);
  const inactiveRows = useMemo(() => rows.filter((r) => r.statusCode === ShiftStatusCode.INACTIVE), [rows]);

  const onCreate = async (values: ShiftFormValues): Promise<boolean> => {
    const payload: CreateShiftPayload = {
      name: values.name,
      code: values.code,
      start_time: values.start_time,
      end_time: values.end_time,
      description: values.description,
    };

    const res = await createShift(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo ca làm việc thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo ca làm việc", type: "error" });
    return false;
  };

  const onEditSubmit = async (values: ShiftFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      ...values,
      status: values.isActive ? ShiftStatusCode.ACTIVE : ShiftStatusCode.INACTIVE,
    };
    delete (payload as any).isActive;

    const res = await updateShift(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật ca làm việc thành công", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật", type: "error" });
    return false;
  };

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;
    const nextStatus = pendingStatus.target === "activate" ? ShiftStatusCode.ACTIVE : ShiftStatusCode.INACTIVE;
    const res = await changeStatusShift(pendingStatus.row.id, {
      status: nextStatus,
      ...(pendingStatus.assignmentCount ? { confirm: true } : {}),
    });
    // Ca còn được gán cho nhân viên từ ngày mai trở đi: giữ modal, hỏi lại trước khi xoá các lượt gán đó.
    if (res && res.status === SHIFT_REQUIRES_CONFIRM_STATUS && (res.data as any)?.requires_confirm) {
      setPendingStatus({ ...pendingStatus, assignmentCount: Number((res.data as any).assignment_count ?? 0) || 1 });
      return;
    }
    if (res && isSuccess(res)) {
      showToast({
        message: pendingStatus.target === "activate" ? "Đã kích hoạt ca làm việc" : "Đã tạm ngưng ca làm việc",
        type: "success",
      });
      setPendingStatus(null);
      void load();
      return;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    setPendingStatus(null);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Danh sách ca làm việc
        </h1>
        <button
          onClick={() => setOpenCreate(true)}
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM CA LÀM VIỆC</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 rounded-lg bg-slate-50 px-6 py-3">
        <button
          onClick={() => setTab("active")}
          className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${tab === "active" ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"
            }`}
        >
          ĐANG HOẠT ĐỘNG
          <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-green-600">{activeRows.length}</span>
        </button>
        <button
          onClick={() => setTab("inactive")}
          className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${tab === "inactive" ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"
            }`}
        >
          TẠM NGƯNG
          <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-slate-600">{inactiveRows.length}</span>
        </button>
      </div>

      {/* Table */}
      <div className="mt-2">
        {tab === "active" ? (
          <ShiftTable
            rows={activeRows}
            isLoading={loadingList}
            onEdit={setEditRow}
            onDeactivate={(row) => setPendingStatus({ row, target: "deactivate" })}
          />
        ) : (
          <ShiftTable
            rows={inactiveRows}
            isLoading={loadingList}
            onEdit={setEditRow}
            onActivate={(row) => setPendingStatus({ row, target: "activate" })}
          />
        )}
      </div>

      {/* Modals */}
      {openCreate && (
        <ShiftCreateModal
          onClose={() => setOpenCreate(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
          mode="create"
        />
      )}

      {editRow && (
        <ShiftCreateModal
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          initialValues={{
            name: editRow.name,
            code: editRow.code,
            start_time: editRow.start_time,
            end_time: editRow.end_time,
            description: editRow.description ?? "",
            isActive: editRow.statusCode === ShiftStatusCode.ACTIVE,
          }}
          mode="edit"
        />
      )}

      {pendingStatus && (
        <ShiftStatusConfirmModal
          mode={pendingStatus.target}
          shiftName={pendingStatus.row.name}
          assignmentCount={pendingStatus.assignmentCount}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus}
        />
      )}
    </div>
  );
};
export default ShiftIndexPage;
